import { useMemo } from "react";

import { useProfile } from "@/context/ProfileContext";
import { useRemoteConfigBoolean } from "@/context/RemoteConfigContext";
import {
  filterSidebarNavFeatures,
  type SidebarNavFeature,
} from "@/config/sidebar-nav";

/**
 * The nav features the signed-in member may see, resolved once from the
 * profile and remote-config gates. Every nav surface (sidebar, mobile tabs,
 * More, ⌘K, Settings sub-nav, section tabs) reads this so they never disagree.
 */
export function useVisibleSidebarNavFeatures(): SidebarNavFeature[] {
  const { businessProfile, memberAccessLevel, memberRole, memberPermissions } =
    useProfile();
  const showIntelligence = useRemoteConfigBoolean("show_intelligence");
  const showPulseCheck = useRemoteConfigBoolean("show_pulse_check");

  return useMemo(
    () =>
      filterSidebarNavFeatures({
        businessProfile,
        remoteConfig: { showIntelligence, showPulseCheck },
        memberAccessLevel,
        memberRole,
        memberPermissions,
      }),
    [
      businessProfile,
      memberAccessLevel,
      memberRole,
      memberPermissions,
      showIntelligence,
      showPulseCheck,
    ],
  );
}

/** One visible feature by id, or undefined when the member can't see it. */
export function useSidebarNavFeature(
  id: string,
): SidebarNavFeature | undefined {
  const features = useVisibleSidebarNavFeatures();
  return useMemo(() => features.find((f) => f.id === id), [features, id]);
}
